import { ColumnsPanelTrigger, FilterPanelTrigger, GridToolbarContainer, GridToolbarProps, QuickFilter, QuickFilterClear, QuickFilterControl, QuickFilterTrigger, useGridApiRef } from "@mui/x-data-grid";
import { Badge, Divider, InputAdornment, styled, TextField, Tooltip, Typography } from "@mui/material";
import { ToolbarButton } from "@mui/x-data-grid";
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CancelIcon from '@mui/icons-material/Cancel';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import React from "react";

const StyledQuickFilter = styled(QuickFilter)({
    '& .MuiDataGrid-toolbar': {
      padding: '0 0 16px 0',
    },
  });
  
const StyledToolbarButton = styled(ToolbarButton)<{ ownerState: OwnerState }>(
({ theme, ownerState }) => ({
    gridArea: '1 / 1',
    width: 'min-content',
    height: 'min-content',
    zIndex: 1,
    opacity: ownerState.expanded ? 0 : 1,
    pointerEvents: ownerState.expanded ? 'none' : 'auto',
    transition: theme.transitions.create(['opacity']),
}),
);

type OwnerState = {
    expanded: boolean;
};

const StyledTextField = styled(TextField)<{
ownerState: OwnerState;
}>(({ theme, ownerState }) => ({
gridArea: '1 / 1',
overflowX: 'clip',
width: ownerState.expanded ? 260 : 'var(--trigger-width)',
opacity: ownerState.expanded ? 1 : 0,
transition: theme.transitions.create(['width', 'opacity']),
marginTop: 1
}));

export default function CustomToolbar(props: any & { apiRef: ReturnType<typeof useGridApiRef>, fullFeatured: boolean }) {
    const { title, url, exportUrl, fullFeatured } = props;
    const apiRef = props.apiRef;
    return (
        <GridToolbarContainer>
            <Typography variant="h6" component="div" sx={{ fontSize: 'clamp(0.9rem, 3vw, 1.1rem) !important', flexGrow: 1 }}>{title}</Typography>
            <Tooltip title="Refresh data">
                <ToolbarButton onClick={() => apiRef.current.dataSource?.fetchRows()}>
                    <RefreshIcon fontSize="small" />
                </ToolbarButton>
            </Tooltip>
            <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} />
            {fullFeatured && (
                <>
                    <Tooltip title="Columns">
                        <ColumnsPanelTrigger render={<ToolbarButton />}>
                            <ViewColumnIcon fontSize="small" />
                        </ColumnsPanelTrigger>
                    </Tooltip>
                    <Tooltip title="Filters">
                        <FilterPanelTrigger
                            render={(props, state) => (
                            <ToolbarButton {...props} color="default">
                                <Badge badgeContent={state.filterCount} color="primary" variant="dot">
                                    <FilterListIcon fontSize="medium" />
                                </Badge>
                            </ToolbarButton>
                            )}
                        />
                    </Tooltip>
                </>
            )}
            <Tooltip title="Export">
                <ToolbarButton onClick={() => window.location.href = exportUrl}>
                    <FileDownloadIcon fontSize="small" />
                </ToolbarButton>
            </Tooltip>
            {fullFeatured && (
                <StyledQuickFilter>
                    <QuickFilterTrigger
                        render={(triggerProps, state) => (
                        <Tooltip title="Search" enterDelay={0}>
                            <StyledToolbarButton
                                {...triggerProps}
                                ownerState={{ expanded: state.expanded }}
                                color="default"
                                aria-disabled={state.expanded}
                            >
                                <SearchIcon fontSize="small" sx={{ marginTop: 0.5 }} />
                            </StyledToolbarButton>
                        </Tooltip>
                        )}
                    />
                    <QuickFilterControl
                        render={({ ref, ...controlProps }, state) => (
                        <StyledTextField
                            {...controlProps}
                            ownerState={{ expanded: state.expanded }}
                            inputRef={ref}
                            aria-label="Search"
                            placeholder="Search..."
                            size="small"
                            variant="outlined"
                            value={state.value}
                            onChange={(event) => {
                                apiRef.current.setQuickFilterValues([event.target.value]);
                            }}
                            label="Press Enter to Search"
                            onKeyDownCapture={async (event) => {
                                if (event.key === 'Enter') {
                                    const response = await fetch(`${url}?entity_id=${localStorage.getItem('entity_id')}&search=${(event.target as HTMLInputElement).value}`, {
                                        method: 'GET',
                                    });
                                    const data = await response.json();
                                    apiRef.current.setRows(data.rows);
                                    apiRef.current.setRowCount(data.rowCount);
                                }
                            }}
                            slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                                endAdornment: state.value ? (
                                <InputAdornment position="end">
                                    <QuickFilterClear
                                        edge="end"
                                        size="small"
                                        aria-label="Clear search"
                                        material={{ sx: { marginRight: -0.75 } }}
                                    >
                                    <CancelIcon fontSize="small" />
                                    </QuickFilterClear>
                                </InputAdornment>
                                ) : null,
                                ...controlProps.slotProps?.input,
                            },
                            ...controlProps.slotProps,
                            }}
                        />
                        )}
                    />
                </StyledQuickFilter>
            )}
        </GridToolbarContainer>
    );
}